import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from 'firebase/functions'
import { getFirestore } from 'firebase/firestore'

export const useBackendStore = defineStore('backend', () => {
  // State
  const db = ref(null)
  const app = ref(null)
  const auth = ref(null)
  const functions = ref(null)
  const initing = ref(true)
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }

  // Getters
  const isIniting = computed(() => initing.value)

  // Actions
  async function initialize() {
    if (!app.value) {
      const firebaseApp = initializeApp(firebaseConfig)
      app.value = firebaseApp

      const firebaseFunctions = getFunctions(firebaseApp)
      if (import.meta.env.VITE_EMULATOR_IP) {
        const emulatorPort = parseInt(import.meta.env.VITE_EMULATOR_PORT) || 5001
        connectFunctionsEmulator(
          firebaseFunctions,
          import.meta.env.VITE_EMULATOR_IP,
          emulatorPort
        )
      }
      functions.value = firebaseFunctions

      auth.value = getAuth(firebaseApp)
      db.value = getFirestore(firebaseApp)

      initing.value = false
    }
  }

  async function geocodeAddress(address) {
    const { useMainStore } = await import('./main')
    const mainStore = useMainStore()

    mainStore.setLoading(true, 'Looking up address...')

    try {
      const fn = httpsCallable(functions.value, 'geocodeAddress')
      const result = await fn({
        address,
        test: import.meta.env.VITE_IP_TEST_MODE == 'true',
      })

      if (!result.data || result.data.status != 200 || !result.data.latLng) {
        throw new Error('Unable to geocode address: ' + result.data.status)
      }

      return result.data
    } catch (e) {
      throw new Error(e.message)
    } finally {
      mainStore.setLoading(false)
    }
  }

  return {
    // State
    db,
    app,
    auth,
    functions,
    initing,
    firebaseConfig,

    // Getters
    isIniting,

    // Actions
    initialize,
    geocodeAddress
  }
})
